import { type ReactNode } from 'react'
import { MagnifyingGlassIcon } from '@heroicons/react/16/solid'
import Heading from '../../typeography/heading'

// Main container component
export interface JosefTitleActionHeaderProps {
  children: ReactNode
  className?: string
}

export function JosefTitleActionHeader({
  children,
  className = '',
}: JosefTitleActionHeaderProps) {
  return (
    <div
      className={`sticky top-0 z-20 flex-shrink-0 rounded-lg mt-2 bg-neutral-50 dark:bg-neutral-900 ${className}`}
    >
      {children}
    </div>
  )
}

// Primary section (title, search, actions)
export interface JosefTitleActionHeaderPrimaryProps {
  title?: string
  titleClassName?: string
  description?: string
  rightActions?: ReactNode
  children?: ReactNode
  breadcrumb?: ReactNode
  search?: {
    placeholder?: string
    onChange?: (value: string) => void
    value?: string
  }
  hasBorder?: boolean
  backButton?: ReactNode
  truncate?: boolean
}

export function JosefTitleActionHeaderPrimary({
  title,
  titleClassName,
  description,
  rightActions,
  children,
  breadcrumb,
  search,
  hasBorder = false,
  backButton,
  truncate = true,
}: JosefTitleActionHeaderPrimaryProps) {
  const isSimpleLayout = !search && !children && !description
  return (
    <div
      className={`px-4 py-3 ${isSimpleLayout ? 'h-full flex items-center' : ''} ${hasBorder ? 'border-b border-black/5 dark:border-white/5' : ''}`}
    >
      <div
        className={`flex items-center gap-3 ${isSimpleLayout ? 'w-full' : ''}`}
      >
        {/* Back Button - Far Left */}
        {backButton ? <div className="flex-shrink-0">{backButton}</div> : null}

        {/* Breadcrumb or Title - Left Side */}
        {breadcrumb ? (
          <div className="flex-shrink-0">{breadcrumb}</div>
        ) : title ? (
          <div className={`flex-1 min-w-0 pr-2 ${titleClassName || ''}`}>
            <div
              className={
                truncate
                  ? 'w-full whitespace-nowrap overflow-hidden text-ellipsis'
                  : ''
              }
            >
              <span className="font-medium text-black dark:text-white whitespace-nowrap">
                {title}
              </span>
            </div>
            {description && (
              <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                {description}
              </p>
            )}
          </div>
        ) : null}

        {/* Search or Children Content - Middle */}
        {search || children ? (
          <div className="flex-1">
            {search ? (
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-500 dark:text-gray-400" />
                <input
                  type="text"
                  placeholder={search.placeholder || 'Search...'}
                  value={search.value || ''}
                  onChange={(e) => search.onChange?.(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 text-[14px] bg-neutral-50 dark:bg-neutral-900 border border-black/10 dark:border-white/10 rounded-md text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500 transition-colors"
                />
              </div>
            ) : (
              children
            )}
          </div>
        ) : rightActions && !title ? (
          <div className="flex-1" />
        ) : null}

        {/* Actions - Right Side */}
        {rightActions ? (
          <div className="flex items-center">{rightActions}</div>
        ) : null}
      </div>
    </div>
  )
}

// Secondary section (filters, status pills, etc.)
export interface JosefTitleActionHeaderSecondaryProps {
  children: ReactNode
  className?: string
}

export function JosefTitleActionHeaderSecondary({
  children,
  className = '',
}: JosefTitleActionHeaderSecondaryProps) {
  return <div className={`px-4 py-3 ${className}`}>{children}</div>
}

// Compound component pattern
JosefTitleActionHeader.Primary = JosefTitleActionHeaderPrimary
JosefTitleActionHeader.Secondary = JosefTitleActionHeaderSecondary

export default JosefTitleActionHeader
